import { ApplicationConfig, provideBrowserGlobalErrorListeners, provideZoneChangeDetection, isDevMode } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { providePrimeNG } from 'primeng/config';
import { provideNgxMask } from 'ngx-mask';
import { MessageService, ConfirmationService } from 'primeng/api';
import { provideStore, provideState } from '@ngrx/store';

import { logoutMetaReducers } from './core/store/clear-state-on-logout.meta-reducer';
import { routes } from './app.routes';
import { errorInterceptor } from './core/interceptors/error.interceptor';
import { bearerTokenInterceptor } from './core/interceptors/bearer-token.interceptor';
import { authRefreshInterceptor } from './core/interceptors/auth-refresh.interceptor';
import { loadingInterceptor } from './core/interceptors/loading.interceptor';
import { isPublicLeaderboardAppRoute } from './core/utils/public-leaderboard.utils';
import Aura from '@primeuix/themes/aura';
import { applyJobFeature } from './features/jobs/apply-job/store/apply-job.reducer';
import { metaReducers as applyJobMetaReducers } from './features/jobs/apply-job/store/apply-job.storage';
import { quickApplicationFeature } from './features/quick-application/store/quick-application.reducer';
import { quickApplicationMetaReducers } from './features/quick-application/store/quick-application.storage';
import { companyListFeature } from './features/company/company-list/store/company-list.reducer';
import { companyListMetaReducers } from './features/company/company-list/store/company-list.storage';
import { jobsListFeature } from './features/jobs/jobs-list/store/jobs-list.reducer';
import { jobsListMetaReducers } from './features/jobs/jobs-list/store/jobs-list.storage';
import { studentsListFeature } from './features/students/students-list/store/students-list.reducer';
import { studentsListMetaReducers } from './features/students/students-list/store/students-list.storage';
import { interviewsListFeature } from './features/interviews/interviews-list/store/interviews-list.reducer';
import { interviewsListMetaReducers } from './features/interviews/interviews-list/store/interviews-list.storage';
import { applicationsListFeature } from './features/applications/applications-list/store/applications-list.reducer';
import { applicationsListMetaReducers } from './features/applications/applications-list/store/applications-list.storage';
import { usersListFeature } from './features/users/users-list/store/users-list.reducer';
import { usersListMetaReducers } from './features/users/users-list/store/users-list.storage';

function isPublicLeaderboardBootPath(): boolean {
  const path = globalThis.location?.pathname ?? '';
  return isPublicLeaderboardAppRoute(path);
}

function unregisterServiceWorkerOnLeaderboardBoot(): void {
  if (!isPublicLeaderboardBootPath()) {
    return;
  }

  const workerContainer = globalThis.navigator?.serviceWorker;
  if (!workerContainer?.getRegistrations) {
    return;
  }

  void workerContainer.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => void registration.unregister());
  });
}

unregisterServiceWorkerOnLeaderboardBoot();

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(
      withInterceptors([
        loadingInterceptor,
        bearerTokenInterceptor,
        errorInterceptor,
        authRefreshInterceptor
      ])
    ),
    provideAnimations(),
    provideStore({}, {
      metaReducers: [
        ...logoutMetaReducers,
        ...applyJobMetaReducers,
        ...quickApplicationMetaReducers,
        ...companyListMetaReducers,
        ...jobsListMetaReducers,
        ...studentsListMetaReducers,
        ...interviewsListMetaReducers,
        ...applicationsListMetaReducers,
        ...usersListMetaReducers
      ]
    }),
    provideState(applyJobFeature),
    provideState(quickApplicationFeature),
    provideState(companyListFeature),
    provideState(jobsListFeature),
    provideState(studentsListFeature),
    provideState(interviewsListFeature),
    provideState(applicationsListFeature),
    provideState(usersListFeature),
    provideServiceWorker('ngsw-worker.js', {
      // TV kiosk browsers have unreliable service workers; skip SW on leaderboard URLs.
      enabled: !isDevMode() && !isPublicLeaderboardBootPath(),
      registrationStrategy: 'registerWhenStable:5000'
    }),
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: false
        }
      }
    }),
    provideNgxMask(),
    MessageService,
    ConfirmationService
  ]
};
